// AdminSettings — system-wide admin settings landing page.
// Surfaces backend health, environment info, and quick links to the
// concrete admin tools (companies, users, finance, integrations, build queue).
//
// Dedicated per-tool pages do the actual configuration:
//   - /admin/integrations  → API tokens + webhook logs (AdminIntegrations.js)
//   - /admin/companies/:id → per-company agent config + billing
//   - /admin/users         → role + access management
//
// Platform-wide toggles (feature flags, default pricing, branding) can be
// added via a /api/admin/settings endpoint when needed. Currently this page
// routes admins to the tools that already exist.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useToast } from '../context/ToastContext';
import {
  Building2, Users, DollarSign, Webhook, Wrench,
  Activity, CheckCircle2, AlertCircle, ExternalLink,
} from 'lucide-react';

const QUICK_LINKS = [
  {
    to: '/admin/companies',
    icon: Building2,
    title: 'Bedrifter',
    desc: 'Administrer kunder, abonnement og AI-agentkonfigurasjon.',
  },
  {
    to: '/admin/users',
    icon: Users,
    title: 'Brukere',
    desc: 'Inviter brukere, sett roller og håndter tilgang.',
  },
  {
    to: '/admin/finance',
    icon: DollarSign,
    title: 'Økonomi',
    desc: 'MRR, fakturering og Stripe-status per kunde.',
  },
  {
    to: '/admin/integrations',
    icon: Webhook,
    title: 'Integrasjoner',
    desc: 'API-nøkler og webhook-leveringslogger.',
  },
  {
    to: '/build-queue',
    icon: Wrench,
    title: 'Byggekø',
    desc: 'Onboarding-oppgaver og leveringsstatus.',
  },
];

export default function AdminSettings() {
  const [health, setHealth] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [h, o] = await Promise.allSettled([
          api.get('/api/health'),
          api.get('/api/admin/overview'),
        ]);
        if (h.status === 'fulfilled') setHealth(h.value.data);
        if (o.status === 'fulfilled') setOverview(o.value.data);
      } catch (err) {
        console.error('AdminSettings load failed', err);
        toast.error('Kunne ikke laste systemstatus');
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const healthy = health && (health.status === 'ok' || health.status === 'healthy' || health.ok === true);
  const apiBase = process.env.REACT_APP_BACKEND_URL || '(samme opphav)';

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
          Innstillinger
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Plattformoversikt og snarveier til administrasjonsverktøy.
        </p>
      </div>

      {/* System status row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
            <Activity size={14} /> Backend-status
          </div>
          {loading ? (
            <div className="text-slate-400 text-sm">Henter…</div>
          ) : healthy ? (
            <div className="flex items-center gap-2 text-accent-green">
              <CheckCircle2 size={18} />
              <span className="text-base font-semibold">Operativ</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-accent-red">
              <AlertCircle size={18} />
              <span className="text-base font-semibold">Utilgjengelig</span>
            </div>
          )}
          <div className="text-xs text-slate-500 mt-2 font-mono break-all">
            {apiBase}
          </div>
        </div>

        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
            <Building2 size={14} /> Aktive bedrifter
          </div>
          <div className="text-2xl font-bold text-white">
            {loading ? '—' : (overview?.active_companies ?? overview?.activeCompanies ?? '—')}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Totalt: {loading ? '—' : (overview?.total_companies ?? overview?.totalCompanies ?? '—')}
          </div>
        </div>

        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
            <Users size={14} /> Brukere
          </div>
          <div className="text-2xl font-bold text-white">
            {loading ? '—' : (overview?.total_users ?? overview?.totalUsers ?? '—')}
          </div>
          <div className="text-xs text-slate-500 mt-1">På tvers av alle bedrifter</div>
        </div>
      </div>

      {/* Quick links to real admin tools */}
      <h2 className="font-heading text-sm uppercase tracking-widest text-slate-500 mb-3">
        Administrasjonsverktøy
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {QUICK_LINKS.map(({ to, icon: Icon, title, desc }) => (
          <Link
            key={to}
            to={to}
            data-testid={`admin-tool-${to.replace(/\//g, '-')}`}
            className="group bg-navy-800 border border-navy-600 rounded-lg p-5 hover:border-accent-blue transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-accent-blue">
                <Icon size={18} />
                <span className="font-semibold text-white">{title}</span>
              </div>
              <ExternalLink
                size={14}
                className="text-slate-600 group-hover:text-accent-blue transition-colors"
              />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
          </Link>
        ))}
      </div>

      <p className="mt-8 text-xs text-slate-600">
        Plattformomfattende konfigurasjon (funksjonsflagg, standardpriser,
        merkevare) er ikke implementert ennå. Per-bedrift-innstillinger
        ligger under <Link to="/admin/companies" className="text-accent-blue hover:underline">Bedrifter</Link>.
      </p>
    </div>
  );
}
